import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import NotificationCard from './components/NotificationCard';
import FilterTabs from './components/FilterTabs';
import {
  fetchNotifications,
  toggleNotificationReadStatus,
  markAllNotificationsAsRead,
  calculateNotificationCounts,
} from '../../services/notificationService';
import { subscribeToNotifications } from '../../services/realtimeService';

const NotificationCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [counts, setCounts] = useState({
    all: 0,
    campaign: 0,
    message: 0,
    payment: 0,
    proposal: 0,
    unread: 0,
  });

  // Fetch notifications
  const loadNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const data = await fetchNotifications(user?.id);
      setNotifications(data);
      setCounts(calculateNotificationCounts(data));
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter notifications
  useEffect(() => {
    let filtered = notifications;

    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered?.filter((n) => n?.category === activeFilter);
    }

    // Apply read/unread filter
    if (showUnreadOnly) {
      filtered = filtered?.filter((n) => !n?.read);
    }

    setFilteredNotifications(filtered);
  }, [notifications, activeFilter, showUnreadOnly]);

  // Real-time subscription
  useEffect(() => {
    if (!user?.id) return;

    loadNotifications();

    const subscription = subscribeToNotifications(user?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        setNotifications((prev) => {
          const updated = [payload?.new, ...prev];
          setCounts(calculateNotificationCounts(updated));
          return updated;
        });
      } else if (payload?.eventType === 'UPDATE') {
        setNotifications((prev) => {
          const updated = prev?.map((n) => (n?.id === payload?.new?.id ? payload?.new : n));
          setCounts(calculateNotificationCounts(updated));
          return updated;
        });
      } else if (payload?.eventType === 'DELETE') {
        setNotifications((prev) => {
          const updated = prev?.filter((n) => n?.id !== payload?.old?.id);
          setCounts(calculateNotificationCounts(updated));
          return updated;
        });
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [user?.id]);

  // Toggle notification read/unread status
  const handleToggleReadStatus = async (notificationId, currentReadStatus) => {
    try {
      const updatedNotification = await toggleNotificationReadStatus(
        notificationId,
        user?.id,
        currentReadStatus
      );

      if (updatedNotification) {
        setNotifications((prev) => {
          const updated = prev?.map((n) =>
            n?.id === notificationId ? updatedNotification : n
          );
          setCounts(calculateNotificationCounts(updated));
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to toggle notification status:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(user?.id);
      setNotifications((prev) => {
        const updated = prev?.map((n) => ({ ...n, read: true }));
        setCounts(calculateNotificationCounts(updated));
        return updated;
      });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Navigate to action URL
  const handleNavigate = (url) => {
    if (url) {
      navigate(url);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Notification Center</h1>
            {counts?.unread > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllAsRead}>
                <Icon name="CheckCheck" size={16} className="mr-2" />
                Mark all as read
              </Button>
            )}
          </div>
          <p className="text-muted-foreground">
            Stay updated with campaign alerts, messages, payments, and proposals
          </p>
        </div>

        {/* Filter Controls */}
        <div className="mb-6 space-y-4">
          <FilterTabs
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            counts={counts}
          />

          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                showUnreadOnly
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={showUnreadOnly ? 'CheckSquare' : 'Square'} size={16} />
              Show unread only ({counts?.unread})
            </button>

            <span className="text-sm text-muted-foreground">
              {filteredNotifications?.length} notification{filteredNotifications?.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading notifications...</p>
            </div>
          </div>
        ) : filteredNotifications?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Bell" size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No notifications</h3>
            <p className="text-muted-foreground">
              {showUnreadOnly
                ? 'You have no unread notifications'
                : activeFilter === 'all' ?'You have no notifications yet'
                : `No ${activeFilter} notifications`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications?.map((notification) => (
              <NotificationCard
                key={notification?.id}
                notification={notification}
                onToggleReadStatus={handleToggleReadStatus}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;